/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { Text, Textarea } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';

interface RecordContactDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (note: string) => Promise<void>;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * Dialog recording that the broker spoke to the client.
 *
 * The note is required, and the backend refuses a blank one: an empty
 * contact says only that someone pressed a button, which is worse than
 * no record at all once it starts feeding the leads-without-return
 * count.
 *
 * This is the first of the two ways out of Novo. The other is listing a
 * property of interest -- and until this existed, it was the only one
 * reachable from the screen, which left the broker who called a client
 * with nothing to show for it.
 *
 * `Textarea`, not `TextAreaInput`: the latter always renders `InputFill`,
 * which reads a form store and throws outside a `StoreProvider`.
 *
 * @param props - Dialog open state and the submit/close callbacks.
 * @returns The record-contact form shell.
 */
export function RecordContactDialog({ open, onClose, onSubmit }: RecordContactDialogProps) {
  const { t } = useTranslation('features/deals');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote('');
    setTouched(false);
  }, [open]);

  const canSubmit = note.trim().length > 0;
  const showError = touched && !canSubmit;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(note.trim());
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('detail.contactDialog.title')}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          loading={submitting}
          label={t('detail.contactDialog.confirm')}
          savingLabel={t('detail.contactDialog.confirming')}
        />
      }
    >
      <div data-testid="record-contact-form" className="flex flex-col gap-(--space-4)">
        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" htmlFor="contact-note" className={labelClasses}>
            {t('detail.contactDialog.note')}
          </Text>
          <Textarea
            id="contact-note"
            data-testid="contact-note-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t('detail.contactDialog.notePlaceholder')}
            autoFocus
          />
          {showError && (
            <Text component="span" className="text-[length:var(--text-meta)] text-(--accent-cobre)">
              {t('detail.contactDialog.noteError')}
            </Text>
          )}
        </fieldset>

        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
          {t('detail.contactDialog.hint')}
        </Text>
      </div>
    </AdaptiveFormShell>
  );
}

export default RecordContactDialog;
